import moment from 'moment';
import 'moment/locale/fr';
import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useAxios } from '../../utils/hook/useAxios';

moment.locale('fr');

function fmtEur(n) {
  return (parseFloat(n) || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
}

export default function PaymentReceipt() {
  const { reservationId } = useParams();
  const [searchParams] = useSearchParams();
  const paymentIntentId = searchParams.get('pi');

  const axios = useAxios();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const receiptRef = useRef(null);

  useEffect(() => {
    axios
      .get('/reservations/getbyid/' + reservationId)
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [reservationId]);

  /* ── Impression / téléchargement ── */
  function handlePrint() {
    window.print();
  }

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="spinner-border text-primary" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-vh-100 d-flex flex-column align-items-center justify-content-center gap-3">
        <i className="bi bi-exclamation-circle text-muted" style={{ fontSize: 48 }}></i>
        <p className="text-muted">Réservation introuvable.</p>
        <Link to="/guest/listing/reservations" className="btn btn-primary rounded-pill">
          Mes réservations
        </Link>
      </div>
    );
  }

  const vehicle = data.Vehicle;
  const model = vehicle?.VehicleModel;
  const host = data.Host;
  const start = moment(data.startDate);
  const end = moment(data.endDate);
  const days = end.diff(start, 'days');
  // Priorité : montant Stripe passé en URL (source de vérité = paymentIntent.amount)
  // Fallback : Paiements BDD, puis prix annonce × jours
  const urlAmount = searchParams.get('amount');
  const amount = urlAmount
    ? parseFloat(urlAmount)
    : (data.Paiements?.[0]?.amount ?? (data.VehiculeAnnonce?.reservationPrice ?? 0) * days);
  const piId = paymentIntentId || data.Paiements?.[0]?.paymentIntentId || data.PaymentIntentId;

  return (
    <>
      {/* Style impression */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          .receipt-card { box-shadow: none !important; border: 1px solid #e5e7eb !important; }
        }
      `}</style>

      <div className="min-vh-100 bg-body-tertiary py-4 px-3">
        <div className="container" style={{ maxWidth: 640 }}>
          {/* Boutons navigation */}
          <div className="d-flex justify-content-between align-items-center mb-4 no-print">
            <Link
              to={'/guest/reservation/' + reservationId}
              className="btn btn-sm btn-outline-secondary rounded-pill"
            >
              <i className="bi bi-arrow-left me-1"></i>Réservation
            </Link>
            <button className="btn btn-sm btn-outline-primary rounded-pill" onClick={handlePrint}>
              <i className="bi bi-printer me-1"></i>Imprimer / PDF
            </button>
          </div>

          {/* Carte reçu */}
          <div
            className="card border-0 shadow-sm rounded-4 overflow-hidden receipt-card"
            ref={receiptRef}
          >
            {/* Header succès */}
            <div
              className="text-white text-center py-4 px-3"
              style={{ background: 'linear-gradient(135deg,#1d4ed8,#2563eb)' }}
            >
              <div
                className="d-flex align-items-center justify-content-center mb-2"
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.2)',
                  margin: '0 auto',
                }}
              >
                <i className="bi bi-check-lg text-white" style={{ fontSize: 28 }}></i>
              </div>
              <h2 className="fw-bold mb-1" style={{ fontSize: 22 }}>
                Paiement confirmé
              </h2>
              <p className="mb-0 text-white" style={{ opacity: 0.8, fontSize: 14 }}>
                Réservation #{reservationId}
              </p>
            </div>

            <div className="card-body p-4">
              {/* Véhicule */}
              <div className="d-flex gap-3 align-items-center mb-4 pb-4 border-bottom">
                {vehicle?.principalPhotos && (
                  <img
                    src={vehicle.principalPhotos}
                    alt="Véhicule"
                    className="rounded-3 flex-shrink-0"
                    style={{ width: 80, height: 64, objectFit: 'cover' }}
                  />
                )}
                <div>
                  <h5 className="fw-bold mb-1" style={{ fontSize: 16 }}>
                    {model?.marque} {model?.modele}
                  </h5>
                  <p className="mb-0 text-muted" style={{ fontSize: 13 }}>
                    <i className="bi bi-person me-1"></i>
                    {host?.firstName} {host?.lastName}
                  </p>
                </div>
              </div>

              {/* Détails réservation */}
              <div className="d-flex flex-column gap-3 mb-4 pb-4 border-bottom">
                <Row
                  label="Départ"
                  value={start.format('dddd D MMMM YYYY')}
                  icon="bi-calendar-event"
                />
                <Row
                  label="Retour"
                  value={end.format('dddd D MMMM YYYY')}
                  icon="bi-calendar-check"
                />
                <Row label="Durée" value={`${days} jour${days > 1 ? 's' : ''}`} icon="bi-clock" />
                <Row label="N° réservation" value={`#${reservationId}`} icon="bi-hash" mono />
                {piId && (
                  <Row label="Référence paiement" value={piId} icon="bi-credit-card" mono small />
                )}
              </div>

              {/* Montant total */}
              <div
                className="d-flex align-items-center justify-content-between py-3 px-4 rounded-3 mb-4"
                style={{ background: '#f0fdf4' }}
              >
                <span className="fw-semibold" style={{ fontSize: 15 }}>
                  Total payé
                </span>
                <span className="fw-bold text-success" style={{ fontSize: 24 }}>
                  {fmtEur(amount)}
                </span>
              </div>

              {/* Statut paiement */}
              <div className="d-flex align-items-center gap-2 mb-4 pb-4 border-bottom">
                <span
                  className="badge bg-success-subtle text-success rounded-pill px-3 py-2"
                  style={{ fontSize: 13 }}
                >
                  <i className="bi bi-check-circle-fill me-1"></i>Paiement reçu
                </span>
                <span className="text-muted" style={{ fontSize: 12 }}>
                  via Stripe · {moment().format('D MMM YYYY à HH:mm')}
                </span>
              </div>

              {/* Prochaines étapes */}
              <div className="mb-4">
                <p className="fw-semibold mb-3" style={{ fontSize: 14 }}>
                  Prochaines étapes
                </p>
                <div className="d-flex flex-column gap-2">
                  {[
                    {
                      icon: 'bi-chat-dots',
                      text: 'Contactez votre hôte via la messagerie pour les détails de remise des clés',
                    },
                    {
                      icon: 'bi-camera',
                      text: 'Au départ, effectuez le check-in avec photos et kilométrage',
                    },
                    {
                      icon: 'bi-star',
                      text: 'À votre retour, laissez un avis pour aider la communauté',
                    },
                  ].map((step, i) => (
                    <div key={i} className="d-flex align-items-start gap-2">
                      <div
                        className="d-flex align-items-center justify-content-center rounded-circle bg-primary bg-opacity-10 flex-shrink-0"
                        style={{ width: 28, height: 28 }}
                      >
                        <i className={`bi ${step.icon} text-primary`} style={{ fontSize: 12 }}></i>
                      </div>
                      <p className="mb-0 text-muted" style={{ fontSize: 13 }}>
                        {step.text}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="d-flex gap-2 flex-column flex-sm-row no-print">
                <Link
                  to={'/guest/reservation/' + reservationId}
                  className="btn btn-primary flex-fill d-flex align-items-center justify-content-center gap-2"
                >
                  <i className="bi bi-arrow-right-circle-fill"></i>
                  Voir ma réservation
                </Link>
                <Link
                  to="/guest/listing/reservations"
                  className="btn btn-outline-secondary flex-fill d-flex align-items-center justify-content-center gap-2"
                >
                  <i className="bi bi-list-ul"></i>
                  Mes réservations
                </Link>
              </div>
            </div>

            {/* Footer reçu */}
            <div className="px-4 py-3 text-center border-top bg-body-tertiary">
              <p className="mb-0 text-muted" style={{ fontSize: 12 }}>
                Moovicar · Ce document fait office de reçu de paiement ·{' '}
                {moment().format('D MMMM YYYY')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function Row({ label, value, icon, mono, small }) {
  return (
    <div className="d-flex align-items-start justify-content-between gap-2">
      <span className="text-muted d-flex align-items-center gap-1" style={{ fontSize: 13 }}>
        <i className={`bi ${icon}`}></i>
        {label}
      </span>
      <span
        className={`fw-semibold text-end ${mono ? 'font-monospace' : ''}`}
        style={{ fontSize: small ? 11 : 13, wordBreak: 'break-all', maxWidth: '60%' }}
      >
        {value}
      </span>
    </div>
  );
}
